import React, { useMemo } from 'react';
import VideocamIcon from '@mui/icons-material/Videocam';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import PeopleIcon from '@mui/icons-material/People';
import FlashOnIcon from '@mui/icons-material/FlashOn';

import { MetricTile, MetricTileGrid } from '../../../components/admin';

const buildMetrics = (stats, navigate) => {
  const cameraHealth = stats?.camera_health || {};
  const totalCameras = cameraHealth.total ?? stats?.cameras ?? 0;
  const onlineCameras = cameraHealth.online ?? 0;
  const awaitingCameras = cameraHealth.awaiting ?? 0;
  const offlineCameras = cameraHealth.offline ?? Math.max(totalCameras - onlineCameras - awaitingCameras, 0);
  const cameraValue = totalCameras ? `${onlineCameras}/${totalCameras}` : `${onlineCameras}`;
  const cameraSubtextParts = [];
  if (awaitingCameras > 0) {
    cameraSubtextParts.push(`${awaitingCameras} awaiting motion`);
  }
  if (offlineCameras > 0) {
    cameraSubtextParts.push(`${offlineCameras} offline`);
  }
  const cameraSubtext = cameraSubtextParts.length
    ? cameraSubtextParts.join(' · ')
    : totalCameras
      ? 'Active streams'
      : 'No cameras registered';
  const cameraSeverity = totalCameras === 0
    ? 'warning'
    : onlineCameras === 0
      ? 'alert'
      : (offlineCameras > 0 || awaitingCameras > 0 ? 'warning' : 'normal');

  return [
    {
      label: 'Cameras Online',
      value: cameraValue,
      icon: <VideocamIcon fontSize="medium" />,
      color: 'primary',
      subtext: cameraSubtext,
      severity: cameraSeverity,
      highlight: true,
      onClick: () => navigate('/cameras?status=offline'),
    },
    {
      label: 'Total Falls (30d)',
      value: stats?.fall_events?.total ?? 0,
      icon: <NotificationsActiveIcon fontSize="medium" />,
      color: 'error',
      subtext: 'Past month',
      severity: (stats?.fall_events?.total ?? 0) > 0 ? 'alert' : 'normal',
      onClick: () => navigate('/fall-events?period=30'),
    },
    {
      label: 'Falls Today',
      value: stats?.fall_events?.today ?? 0,
      icon: <FlashOnIcon fontSize="medium" />,
      color: 'warning',
      subtext: 'View incidents',
      severity: (stats?.fall_events?.today ?? 0) > 0 ? 'alert' : 'normal',
      onClick: () => navigate('/fall-events?period=1'),
    },
    {
      label: 'Open Enquiries',
      value: stats?.contact_submissions?.open ?? 0,
      icon: <ContactMailIcon fontSize="medium" />,
      color: 'info',
      subtext: 'Awaiting reply',
      severity: (stats?.contact_submissions?.open ?? 0) > 5 ? 'warning' : 'normal',
      onClick: () => navigate('/contact-submissions?status=new'),
    },
    {
      label: 'Active Staff',
      value: stats?.staff ?? 0,
      icon: <PeopleIcon fontSize="medium" />,
      color: 'success',
      subtext: 'Logged in today',
      severity: 'normal',
      onClick: null,
    },
  ];
};

const KpiGrid = ({ stats, navigate }) => {
  const metrics = useMemo(() => buildMetrics(stats, navigate), [stats, navigate]);

  return (
    <MetricTileGrid>
      {metrics.map((metric) => (
        <MetricTile key={metric.label} {...metric} tooltip={metric.subtext || metric.label} />
      ))}
    </MetricTileGrid>
  );
};

export default KpiGrid;
